/**
 * A plan's PATTERN WEEK: the first phase, drawn as seven weekdays or as one tile per day of a cycle.
 *
 * Promoted out of `pages/plan-library/model/inspectorView.ts` on 23 September 2026, when „Nowy
 * program"'s template preview became its second consumer — a feature may not import a page. The
 * rules below are that module's, moved unchanged; the inspector still reaches them through
 * `toInspectorView`, which is where their tests live.
 *
 * ── The FIRST phase, and only the first ──────────────────────────────────────────────────────
 * Owner decision, 3 September 2026: a preview shows a plan's pattern week, not its whole arc. A
 * plan has phases that differ — a deload week is a different week — and drawing all of them would
 * be the builder rather than a preview. The first phase is the one the client starts on.
 *
 * "First" is by `order`, not by array position. The two agree today (the backend emits phases in
 * order and `plan_phase_orders_not_contiguous` requires exactly `1..N`), and reading the field the
 * contract actually defines costs one comparator.
 *
 * ── Two canvases, chosen by an ALLOW-LIST on the mode ────────────────────────────────────────
 * A weekday grid can only be drawn for a mode this build knows has a weekday axis, so
 * `schedulingModeIs(mode, 'date')` selects it and everything else — including a mode this build
 * has never heard of — falls to the cycle's own numbered days. Printing `Pn / Wt / Śr` over a plan
 * with no weekday axis would be a claim about it rather than a conservative reading.
 *
 * A plan with no phase at all draws SEVEN empty weekday tiles whatever its mode says. It has no
 * days, so it has no cycle length either, and a `Cykl wzorcowy · 0 dni` beside an empty grid is a
 * figure where an absence belongs.
 *
 * ── A day is present, free, or on no weekday at all ──────────────────────────────────────────
 * The wire permits a day with no `dayOfWeek`, one outside 1–7, and a second day claiming a weekday
 * another already holds. Here the grid simply has no cell for them: the first day claiming a
 * weekday holds it and the rest do not appear. A preview that reported a scheduling defect would be
 * saying something the builder says properly.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`. Counts leave as numbers.
 */

import type {
  TrainWithMeModulesPlansDtosResponsesDayTemplateResponse as DayTemplate,
  TrainWithMeModulesPlansDtosResponsesPhaseTemplateResponse as PhaseTemplate,
  TrainWithMeModulesPlansDtosResponsesPlanTemplateFullResponse as PlanTemplateFullResponse,
} from '~/shared/api/generated/model';

import { schedulingModeIs } from './planEnums';
import { WEEKDAYS } from './weekProjection';

/**
 * What the caption over a tile names.
 *
 * A discriminated value rather than a string: `Pn` is a locale key and `Dz. 3` interpolates a
 * number, and neither can travel through a key-only translate as a sentence.
 */
export type TileCaption =
  | { readonly kind: 'weekday'; readonly weekday: number }
  | { readonly kind: 'ordinal'; readonly position: number };

/** The day a tile stands for, when the plan puts one there. */
export interface TileDay {
  readonly name: string;
  /** Every exercise the day prescribes, across all of its sessions. Zero is a real answer. */
  readonly exerciseCount: number;
}

export interface WeekTile {
  readonly caption: TileCaption;
  /** `null` is the canon's `wolne`: the plan deliberately leaves this slot free. */
  readonly day: TileDay | null;
}

/**
 * What the section over the tiles is called: a week, or a cycle of a stated length.
 *
 * The day count rides on the label rather than being read off `weekTiles.length` at the call site,
 * because the two are the same number only for a cycle — a week is seven tiles whatever the plan
 * puts in them.
 */
export type WeekLabel =
  | { readonly kind: 'week' }
  | { readonly kind: 'cycle'; readonly days: number };

/** The tiles and what the section over them is called. */
export interface PatternWeekView {
  readonly weekTiles: readonly WeekTile[];
  readonly weekLabel: WeekLabel;
}

/** The name a day wears when it carries none. Named here so the tile cannot invent one. */
const UNNAMED_DAY = '';

/** The phase the client starts on: lowest `order`, array position breaking a tie. */
function firstPhase(phases: readonly PhaseTemplate[]): PhaseTemplate | null {
  return (
    phases
      .map((phase, index) => ({ phase, index }))
      .sort((a, b) => (a.phase.order ?? 0) - (b.phase.order ?? 0) || a.index - b.index)[0]?.phase ??
    null
  );
}

/** How many exercises a day prescribes, across every session in it. */
export function dayExerciseCount(day: DayTemplate): number {
  return (day.sessions ?? []).reduce((sum, session) => sum + (session.exercises ?? []).length, 0);
}

function tileDay(day: DayTemplate): TileDay {
  return { name: day.name?.trim() ?? UNNAMED_DAY, exerciseCount: dayExerciseCount(day) };
}

/** Seven tiles, Monday first, each holding the FIRST day that claimed its weekday. */
function weekdayTiles(days: readonly DayTemplate[]): WeekTile[] {
  const claimed = new Map<number, DayTemplate>();
  for (const day of days) {
    const weekday = day.dayOfWeek;
    if (weekday == null || !WEEKDAYS.includes(weekday)) continue;
    if (claimed.has(weekday)) continue;
    claimed.set(weekday, day);
  }

  return WEEKDAYS.map((weekday) => {
    const day = claimed.get(weekday);
    return {
      caption: { kind: 'weekday', weekday } as const,
      day: day === undefined ? null : tileDay(day),
    };
  });
}

/** One numbered tile per day of the cycle, in the order the plan runs them. */
function cycleTiles(days: readonly DayTemplate[]): WeekTile[] {
  return [...days]
    .map((day, index) => ({ day, index }))
    .sort((a, b) => (a.day.order ?? 0) - (b.day.order ?? 0) || a.index - b.index)
    .map(({ day }, position) => ({
      caption: { kind: 'ordinal', position: position + 1 } as const,
      day: tileDay(day),
    }));
}

/** Seven free weekdays — the shape a plan with nothing in it takes. See the header. */
function emptyWeek(): WeekTile[] {
  return WEEKDAYS.map((weekday) => ({ caption: { kind: 'weekday', weekday }, day: null }));
}

/** The plan's pattern week, from its full read. */
export function patternWeekOf(response: PlanTemplateFullResponse): PatternWeekView {
  const phase = firstPhase(response.phases ?? []);
  if (phase === null) return { weekTiles: emptyWeek(), weekLabel: { kind: 'week' } };

  const days = phase.days ?? [];
  if (schedulingModeIs(response.schedulingMode, 'date')) {
    return { weekTiles: weekdayTiles(days), weekLabel: { kind: 'week' } };
  }

  const tiles = cycleTiles(days);
  return { weekTiles: tiles, weekLabel: { kind: 'cycle', days: tiles.length } };
}
